<template>
  <BModal
    v-model="modal"
    ref="modalRef"
    ok-title="Save"
    ok-variant="success"
    @ok="handleAssignationSubmission"
  >
    <template #title>
      <small>
        <strong :class="txn.amount < 0 ? 'text-danger' : 'text-success'">{{
          formatCurrencyDollarSign(txn.amount)
        }}</strong> </small
      >&nbsp; <small>{{ txn.description }}</small
      >&nbsp;
      <small class="text-muted">{{ txn.date }}</small>
    </template>
    <div class="text-center">
      <BFormGroup
        id="input-group-trsxn-type"
        label="Trsxn Type:"
        label-for="trsxn-type-options"
        class="floating-group"
      >
        <BFormSelect
          id="trsxn-type-options"
          :modelValue="selectedTrsxnType"
          :options="trsxnTypeOpts"
          size="sm"
          style="font-size: 0.85rem"
          @update:modelValue="handleTrsxnTypeChange"
        />
      </BFormGroup>
      <template v-if="selectedTrsxnType === 'parent'">
        <h6 class="mt-3">
          Step 0: Identify Transaction's Children
          <span
            v-b-tooltip.click
            title="Payment's are parents. Purchases and refunds are children. Trsxns from Lending club that went for a payment are also parents."
            class="text-muted d-inline-flex"
          >
            <IconInfoCircle :size="14" />
          </span>
        </h6>
        <p v-if="selectedTrsxnsError" class="small mb-0 text-danger">
          {{ selectedTrsxnsError }}
        </p>
        <IdentifyPaymentTrsxnsView
          :txn="txn"
          @updateSelectedTrsxns="handleSelectedTrsxnsUpdate"
        />
      </template>
      <h6 class="mt-3">Step 1: Category(ies) Assignation</h6>
      <BFormGroup
        id="input-group-spending-categories"
        label="Category(ies):"
        label-for="spending-categories"
        class="floating-group"
      >
        <BFormSelect
          id="spending-categories"
          :modelValue="selectedCategory"
          :options="spendCategoriesOpts"
          size="sm"
          style="font-size: 0.85rem"
          :class="{ 'is-invalid': selectedCategoriesError !== null }"
          @update:modelValue="handleCategorySelection"
        />
      </BFormGroup>
      <div class="small d-flex justify-content-between align-items-center">
        <p class="mb-0 text-danger">
          <span v-if="selectedCategoriesError">{{
            selectedCategoriesError
          }}</span>
        </p>

        <BButton
          :disabled="Object.keys(selectedCategories).length === 0"
          class="text-success"
          variant="link"
          size="sm"
          @click="handleCategoriesSignsFlip"
          >Flip Signs</BButton
        >
      </div>
      <BFormGroup
        v-for="(cat, catUid) in selectedCategories"
        :key="catUid"
        :id="`input-group-spending-category-${catUid}`"
        :label="`${cat.name} Amount:`"
        :label-for="`spending-category-${catUid}`"
        class="floating-group px-3"
      >
        <div class="d-flex align-items-center">
          <BFormInput
            v-model.number="cat.amount"
            type="number"
            step="0.01"
            size="sm"
            placeholder="0.00"
            style="font-size: 0.78rem"
          />
          <IconTrash
            size="16"
            class="text-danger ms-2"
            style="cursor: pointer"
            @click="handleCategoryDeselection(catUid)"
          />
        </div>
      </BFormGroup>
    </div>
    <div v-if="selectedTrsxnType === 'child'" class="text-center">
      <h6 class="mt-3">Step 2: Weekly Output(s) Assignation</h6>
      <div class="d-flex align-items-center">
        <BFormGroup
          id="input-group-weekly-outputs"
          label="Weekly Output(s):"
          label-for="weekly-outputs"
          class="floating-group flex-grow-1 me-2"
        >
          <BFormSelect
            id="weekly-outputs"
            :modelValue="selectedWeek"
            :options="weeksAsOpts"
            size="sm"
            style="font-size: 0.85rem"
            :class="{ 'is-invalid': selectedWeeksError !== null }"
            @update:modelValue="handleWeekSelection"
          />
        </BFormGroup>
        <BButton
          variant="outline-primary"
          class="ms-2 ms-auto mt-2"
          size="sm"
          @click="handleWeekSelection(thisMonday)"
        >
          This Week
        </BButton>
      </div>
      <p v-if="selectedWeeksError" class="small mb-0 text-danger">
        {{ selectedWeeksError }}
      </p>
      <BFormGroup
        v-for="(weekObj, week) in selectedWeeks"
        :key="week"
        :id="`input-group-weekly-output-${week}`"
        :label="`${week}'s Amount:`"
        :label-for="`weekly-output-${week}`"
        class="floating-group px-3"
      >
        <div class="d-flex align-items-center">
          <BFormInput
            v-model.number="weekObj.amount"
            type="number"
            step="0.01"
            size="sm"
            placeholder="0.00"
            style="font-size: 0.78rem"
          />
          <IconTrash
            size="16"
            class="text-danger ms-2"
            style="cursor: pointer"
            @click="handleWeekDeselection(week)"
          />
        </div>
      </BFormGroup>
    </div>
    <div class="text-center">
      <h6 class="mt-3">Step 3: Transaction Details</h6>
      <BFormGroup
        id="input-group-note"
        label="Notes:"
        label-for="note-input"
        class="floating-group"
      >
        <BFormTextarea
          id="note-input"
          v-model="note"
          :class="{ 'is-invalid': noteError !== null }"
          placeholder="Enter something..."
          rows="3"
          style="font-size: 0.85rem"
        />
      </BFormGroup>
    </div>
  </BModal>
</template>
<script>
import IdentifyPaymentTrsxnsView from "@/components/Transactions/IdentifyPaymentTrsxnsView.vue";
import numberFormatMixin from "@/mixins/numberFormatMixin";
import {
  BModal,
  BFormSelect,
  BFormGroup,
  BButton,
  BFormInput,
  BFormTextarea,
  vBTooltip,
} from "bootstrap-vue-next";
import { IconTrash, IconInfoCircle } from "@tabler/icons-vue";

const selectedCategoryObj = {
  uid: null,
  name: null,
  amount: null,
};

const weeklyOutputObj = {
  week: null,
  amount: null,
};

const numsAreEqual = (amt1, amt2, useAbs = true) => {
  const val1 = useAbs ? Math.abs(amt1) : amt1;
  const val2 = useAbs ? Math.abs(amt2) : amt2;
  return Math.abs(val1 - val2) < 0.01;
};

export default {
  name: "TransactionsCreateAssignmentModal",
  props: {
    txn: {
      type: Object,
      required: true,
    },
    weeksAsOpts: {
      type: Array,
      required: true,
    },
    thisMonday: {
      type: String,
      required: true,
    },
    spendCategoriesOpts: {
      type: Array,
      required: true,
    },
  },
  emits: ["assignationSaved"],
  mixins: [numberFormatMixin],
  directives: {
    BTooltip: vBTooltip,
  },
  components: {
    IconTrash,
    BModal,
    BFormInput,
    BButton,
    BFormSelect,
    BFormGroup,
    IdentifyPaymentTrsxnsView,
    IconInfoCircle,
    BFormTextarea,
  },
  data() {
    return {
      modal: false,
      selectedCategory: null,
      selectedCategories: {},
      selectedWeek: null,
      selectedWeeks: {},
      selectedTrsxns: {},
      selectedTrsxnType: "child", // Default to "child", but the user can change it as needed
      trsxnTypeOpts: [
        { value: "child", text: "Child" },
        { value: "parent", text: "Parent" },
      ],
      note: null,
      noteError: null, // Is optional
    };
  },
  computed: {
    catsUidToName() {
      const mapping = {};
      this.spendCategoriesOpts.forEach((opt) => {
        if (opt.value) {
          mapping[opt.value.uid] = opt.text;
        }
      });
      return mapping;
    },
    selectedCategoriesError() {
      if (Object.keys(this.selectedCategories).length === 0) {
        return "At least one category must be assigned.";
      }

      const cats = this.selectedCategories;
      const catsAmnts = Object.values(cats).map((cat) => cat.amount);
      const amntsSum = catsAmnts.reduce((sum, amt) => sum + (amt || 0), 0);
      const equal = amntsSum.toFixed(2) === this.txn.amount.toFixed(2);
      const amtErr = `Total assigned does not match transaction amount`;

      if (!equal) return amtErr;

      // Ensure no category has an amount of 0
      const zeroAmntCat = Object.values(cats).find((cat) => !cat.amount);
      const zeroAmntErr = "Assigned categories cannot have an amount of 0";
      if (zeroAmntCat) return zeroAmntErr;

      return null;
    },
    selectedWeeksError() {
      if (Object.keys(this.selectedWeeks).length === 0) return null;

      // Ensure no week has an amount of 0
      const weeks = this.selectedWeeks;
      const zeroAmntWeek = Object.values(weeks).find((week) => !week.amount);
      const zeroAmntErr = "Assigned weeks cannot have an amount of 0";
      if (zeroAmntWeek) return zeroAmntErr;

      // Ensure total assigned to weeks does not exceed transaction amount
      const weeksAmnts = Object.values(weeks).map((week) => week.amount);
      const amntsSum = weeksAmnts.reduce((sum, amt) => sum + (amt || 0), 0);
      const exceedsErr = `Total assigned to weeks cannot exceed transaction amount`;
      if (Math.abs(amntsSum) > Math.abs(this.txn.amount)) return exceedsErr;

      return null;
    },
    selectedTrsxnsError() {
      if (this.selectedTrsxnType !== "parent") return null; // Only validate if the transaction is a parent (payment)

      if (Object.keys(this.selectedTrsxns).length === 0) {
        return "At least one transaction must be identified as part of the payment.";
      }

      const trsxnsSum = Object.values(this.selectedTrsxns).reduce(
        (sum, txn) => sum + txn.amount,
        0
      );

      if (!numsAreEqual(trsxnsSum, this.txn.amount)) {
        return "The sum of the selected transactions must equal the payment transaction amount.";
      }

      return null;
    },
  },
  methods: {
    handleCategorySelection(cat) {
      this.selectedCategory = null; // Reset the select input
      if (cat && !this.selectedCategories[cat.uid]) {
        this.selectedCategories[cat.uid] = {
          ...selectedCategoryObj, // Ensure core properties are included
          ...cat, // Override with actual category data (uid, name, etc.)
        };

        // If only one category in the list, auto-assign the full amount to it
        if (Object.keys(this.selectedCategories).length === 1) {
          this.selectedCategories[cat.uid].amount = this.txn.amount;
        }
      }
    },
    handleCategoryDeselection(catUid) {
      delete this.selectedCategories[catUid];
    },
    handleWeekSelection(week) {
      this.selectedWeek = null; // Reset the select input
      if (!week) return;

      if (!this.selectedWeeks[week]) {
        this.selectedWeeks[week] = { ...weeklyOutputObj, week };

        if (Object.keys(this.selectedWeeks).length === 1) {
          this.selectedWeeks[week].amount = this.txn.amount;
        }
      }
    },
    handleWeekDeselection(week) {
      delete this.selectedWeeks[week];
    },
    async handleAssignationSubmission(bvEvt) {
      try {
        bvEvt.preventDefault(); // Prevent the modal from closing immediately
        // If there are validation errors, do not proceed with submission
        if (
          this.selectedCategoriesError ||
          this.selectedWeeksError ||
          this.selectedTrsxnsError
        )
          return;

        // Prepare the payload for submission
        const payload = {
          acct_uid: this.txn.acct_uid,
          // Assigned categories with their amounts
          snapshot_categories: Object.values(this.selectedCategories).map(
            (cat) => ({
              spending_category_uid: cat.uid,
              fixed_amount: cat.amount.toFixed(2), // Ensure amount is sent as a string with 2 decimal places
            })
          ),
          // Assigned weekly outputs with their amounts
          weekly_outlook_assignments: Object.values(this.selectedWeeks).map(
            (week) => ({
              acct_uid: this.txn.acct_uid,
              weekly_outlook: week.week,
              amount: week.amount.toFixed(2), // Ensure amount is sent as a string with 2 decimal places
            })
          ),
          // UIDs of the transactions identified as part of the payment
          child_trnsxn_uids: Object.keys(this.selectedTrsxns),
          // Transaction additional details (only 'note' for now)
          ...(this.note && this.note.trim() !== "" ? { note: this.note } : {}),
        };

        const response = await this.$api.patch(
          `/banking-institutions/transactions/${this.txn.transaction_uid}`,
          payload
        );

        this.$refs.modalRef.hide(); // Close the modal after successful submission
        this.$notify.notifySuccess(
          "Transaction assignation saved successfully."
        );
        this.$emit("assignationSaved", this.txn.transaction_uid); // Emit an event to notify parent
      } catch (error) {
        console.error("Error submitting transaction assignation:", error);
        this.$notify.notifyError(
          "An error occurred while saving the transaction assignation. Please try again."
        );
      }
    },
    handleSelectedTrsxnsUpdate(selectedTrsxns) {
      this.selectedTrsxns = { ...selectedTrsxns };
      this.chooseSpendingCatsBasedOnSelectedTrsxns();
    },
    chooseSpendingCatsBasedOnSelectedTrsxns() {
      // This method looks at the selected transactions and their categories, and based on that it makes suggestions for which categories to assign to the payment transaction. The user can then adjust those suggestions as they see fit.

      const trsxns = Object.values(this.selectedTrsxns);
      if (trsxns.length === 0) {
        this.selectedCategories = {};
        return;
      }

      const categories = {};

      trsxns.forEach((txn) => {
        if (txn.snapshot_categories) {
          txn.snapshot_categories.forEach((cat) => {
            const catId = cat.spending_category_uid;

            const catAmt = cat.fixed_amount;
            if (!categories[catId]) {
              categories[catId] = {
                ...selectedCategoryObj,
                uid: catId,
                amount: 0,
                name: this.catsUidToName[catId] || "Unknown Category",
              };
            }
            categories[catId].amount += catAmt || 0;
          });
        }
      });

      this.selectedCategories = { ...categories };
    },
    handleTrsxnTypeChange(newType) {
      this.selectedTrsxnType = newType;
      // Reset selected transactions and categories when transaction type changes
      this.selectedTrsxns = {};
      this.selectedCategories = {};
    },
    handleCategoriesSignsFlip() {
      Object.values(this.selectedCategories).forEach((cat) => {
        cat.amount = -cat.amount;
      });
    },
    assignSplurgesAsDefault() {
      // This method searches to see if there is a splurges category in the options, and if there is, it assigns the splurges category to the transaction.

      const splurgeCategory = this.spendCategoriesOpts.find((opt) =>
        opt.text.toLowerCase().includes("splurge")
      );

      if (!splurgeCategory) {
        console.warn(
          "No category with 'Splurge' in its name found. Cannot assign as default."
        );
        return;
      }

      this.handleCategorySelection(splurgeCategory.value);
    },
  },
};
</script>
